import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  MapPin,
  Phone,
  Mail,
  Facebook,
  Send,
  Instagram,
  Youtube,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";

const ContactFooter = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);

    // Simulate sending
    setTimeout(() => {
      toast.success("Mesazhi juaj u dërgua me sukses!");
      setFormData({ name: "", email: "", message: "" });
      setSending(false);
    }, 1000);
  };

  return (
    <footer
      id="kontakti"
      className="bg-primary text-white"
      data-testid="contact-footer"
    >
      <div className="container mx-auto px-4 md:px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-12 mb-12">
          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h3 className="font-heading text-2xl font-bold mb-6 text-accent-100">
              Kontakti
            </h3>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-accent flex-shrink-0 mt-1" />
                <div>
                  <p className="font-semibold mb-1">Adresa</p>
                  <p className="text-white/80">
                    Rruga e Ali Demit, Tiranë, Shqipëri
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Phone className="w-5 h-5 text-accent flex-shrink-0 mt-1" />
                <div>
                  <p className="font-semibold mb-1">Telefoni</p>
                  <p className="text-white/80">+355 67 410 0517</p>
                </div>
              </div>
              <div className="pt-4 flex items-center gap-4">
                <a
                  href="mailto:info@planify.al"
                  className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-accent hover:text-white transition-all shadow-sm"
                  title="Email"
                  data-testid="email-icon-link"
                >
                  <Mail className="w-5 h-5" />
                </a>
                <a
                  href="https://www.facebook.com/xhamiaAliDemit/?locale=sq_AL"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-[#1877F2] hover:text-white transition-all shadow-sm"
                  title="Facebook"
                  data-testid="facebook-icon-link"
                >
                  <Facebook className="w-5 h-5" />
                </a>
                <a
                  href="https://www.instagram.com/explore/locations/353699876/xhamia-ali-demi---faqja-zyrtare/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-[#E4405F] hover:text-white transition-all shadow-sm"
                  title="Instagram"
                  data-testid="instagram-icon-link"
                >
                  <Instagram className="w-5 h-5" />
                </a>
                <a
                  href="https://www.youtube.com/@leonardcela"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-[#FF0000] hover:text-white transition-all shadow-sm"
                  title="YouTube"
                  data-testid="youtube-icon-link"
                >
                  <Youtube className="w-5 h-5" />
                </a>
              </div>
            </div>
          </motion.div>

          {/* Map Placeholder */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <h3 className="font-heading text-2xl font-bold mb-6 text-accent-100">
              Lokacioni
            </h3>
            <div
              className="bg-white/10 rounded-xl overflow-hidden h-64"
              data-testid="map-placeholder"
            >
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d994.6247327957855!2d19.8435248458734!3d41.3262950198503!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x13503100275810f1%3A0x48aaf84578027f0c!2sXhamia%20LAGJES%20mosque!5e0!3m2!1sen!2sus!4v1773675210718!5m2!1sen!2sus"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                title="Xhamia e Ali Demit Location"
              />
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <h3 className="font-heading text-2xl font-bold mb-6 text-accent-100">
              Dërgo Mesazh
            </h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <Input
                placeholder="Emri juaj"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                required
                className="bg-white/10 border-white/20 text-white placeholder:text-white/60"
                data-testid="contact-name-input"
              />
              <Input
                type="email"
                placeholder="Email"
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
                required
                className="bg-white/10 border-white/20 text-white placeholder:text-white/60"
                data-testid="contact-email-input"
              />
              <Textarea
                placeholder="Mesazhi juaj"
                value={formData.message}
                onChange={(e) =>
                  setFormData({ ...formData, message: e.target.value })
                }
                required
                rows={4}
                className="bg-white/10 border-white/20 text-white placeholder:text-white/60"
                data-testid="contact-message-input"
              />
              <Button
                type="submit"
                disabled={sending}
                className="w-full bg-accent hover:bg-accent-700 text-white"
                data-testid="contact-submit-button"
              >
                {sending ? (
                  "Duke dërguar..."
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Dërgo Mesazhin
                  </>
                )}
              </Button>
            </form>
          </motion.div>
        </div>

        {/* Footer Bottom */}
        <div className="border-t border-white/20 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-white/60 text-sm">
              © 2026 Xhamia e Ali Demit. Të gjitha të drejtat e rezervuara.
            </p>
            <div className="flex gap-4 text-xs text-white/40">
              <Link
                to="/privacy"
                className="hover:text-accent transition-colors"
              >
                Politika e Privatësisë
              </Link>
              <Link to="/terms" className="hover:text-accent transition-colors">
                Kushtet e Përdorimit
              </Link>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-white/20 bg-white shadow-sm">
                <img
                  src="/logo192.png"
                  alt="Xhamia Logo"
                  className="w-full h-full object-cover"
                />
              </div>
              <span className="font-heading font-bold">Xhamia e Ali Demit</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default ContactFooter;
